/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import Image from 'next/image'
import { StructuredText } from 'react-datocms'
import classNames from 'classnames'
import { vercelSplit } from '@web/cms'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
	Button,
	type ButtonProps,
	StandaloneLink,
	type StandaloneLinkProps,
	Text,
} from '@hashicorp/mds-react/components'
import { type HeroFullBleedFieldsFragment } from 'types/dato'
import formatButtonToProps from 'lib/dato/formatting/button'
import { Layout } from 'components/materia/layout'
import { isEmptyDocumentV } from 'lib/dato/is-empty-document-v'
import Eyebrow from 'components/materia/sub-blocks/eyebrow'
import formatStandaloneLinkToProps from 'lib/dato/formatting/standaloneLink'
import s from './style.module.css'

const HeroFullBleed = ({
	layout,
	alignment = 'left',
	logo,
	eyebrow,
	heading,
	subheading,
	description,
	backgroundImage,
	cta,
}: HeroFullBleedFieldsFragment) => {
	const alignmentString = vercelSplit(alignment) as 'center' | 'left'

	return (
		<Layout className={s.section} layout={layout}>
			<div className={s.root}>
				<div className={s.inner}>
					<div
						className={classNames(s.content, {
							[s.centered]: alignmentString === 'center',
						})}
					>
						<div className={s.copy}>
							<div className={s.headingWithEyebrow}>
								{eyebrow ? (
									<Eyebrow eyebrow={eyebrow} className={s.eyebrow} />
								) : null}

								{logo ? (
									<div className={s.logotype}>
										<Image
											src={logo.src}
											width={logo.width}
											height={logo.height}
											alt={logo.alt ? vercelSplit(logo.alt) : ''}
										/>
										<VisuallyHidden>
											<h1>{heading}</h1>
										</VisuallyHidden>
									</div>
								) : (
									<Text.DisplayExpressive tag="h1" size="600" color="strong">
										{heading}
									</Text.DisplayExpressive>
								)}
							</div>

							{subheading ? (
								<Text.DisplayExpressive size="300" weight="bold" color="strong">
									{subheading}
								</Text.DisplayExpressive>
							) : null}

							{!isEmptyDocumentV(description) ? (
								<Text.Body color="faint" className={s.description}>
									<StructuredText data={description} />
								</Text.Body>
							) : null}
						</div>

						{cta && cta.length > 0 ? (
							<div className={s.ctas}>
								{cta.map((cta, key) => (
									<Cta key={`${key}-${cta.text}`} {...cta} />
								))}
							</div>
						) : null}
					</div>
					<div className={s.backgroundImage}>
						<Image
							src={backgroundImage.src}
							width={backgroundImage.width}
							height={backgroundImage.height}
							alt={backgroundImage.alt ? vercelSplit(backgroundImage.alt) : ''}
						/>
					</div>
				</div>
			</div>
		</Layout>
	)
}

const Cta = (cta: HeroFullBleedFieldsFragment['cta'][number]) => {
	switch (cta.__typename) {
		case 'StandaloneLinkRecord': {
			const formattedCta = formatStandaloneLinkToProps(cta)
			const props: StandaloneLinkProps = {
				...formattedCta,
				iconPosition: formattedCta.iconPosition || 'trailing',
				size: 'large',
			}

			return <StandaloneLink {...props} />
		}
		case 'ButtonV2Record': {
			const formattedButtonProps = formatButtonToProps(cta)
			const props: ButtonProps = {
				...formattedButtonProps,
				iconPosition: formattedButtonProps.iconPosition || 'trailing',
				size: 'large',
			}
			return <Button {...props} />
		}
	}
}

export default HeroFullBleed
